import React, { useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { Button, Tooltip, Box, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface CellButtonWithCopyProps {
  params: any;
  onClick: (params: any, clickedElement?: string) => void;
  children: React.ReactNode;
}

const CellButtonWithCopy = ({ params, onClick, children }: CellButtonWithCopyProps) => {
  const text = children != null ? String(children) : '';
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 3000);
    } catch {
      // no-op: clipboard write failed (e.g. permissions); keep UI unchanged
    }
  };

  useLayoutEffect(() => {
    checkTruncation();
  }, [text, checkTruncation]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <Tooltip title={text} disableHoverListener={!isTruncated}>
          {/* Tooltip child must span full cell width or the grid centers a shrink-wrapped button */}
          <Box component="span" sx={{ display: 'block', width: '100%', minWidth: 0 }}>
            <Button
              variant="text"
              size="small"
              tabIndex={params.hasFocus ? 0 : -1}
              onClick={e => {
                e.stopPropagation();
                onClick(params);
              }}
              sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                justifyContent: 'flex-start',
                color: 'text.primary',
                textDecoration: 'underline',
                textTransform: 'none',
                fontSize: '14px',
                px: 0.5,
                textAlign: 'left',
                '& .MuiButton-label': {
                  overflow: 'hidden',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  display: 'flex',
                  width: '100%',
                },
              }}
            >
              <Box
                component="span"
                ref={textRef}
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                {text || '—'}
              </Box>
            </Button>
          </Box>
        </Tooltip>
      </Box>

      {text ? (
        <Box sx={{ flexShrink: 0, ml: 0.5 }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton
              onClick={handleCopy}
              size="small"
              aria-label="copy lead details"
              sx={{ color: copied ? 'success.main' : 'inherit' }}
            >
              {copied ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
};

export default CellButtonWithCopy;
